package fr.manyverse.app.presentation.viewmodel

import fr.manyverse.app.domain.model.GameTable
import fr.manyverse.app.domain.model.MemberRole
import fr.manyverse.app.domain.usecase.GetAllUserActiveTablesUseCase
import fr.manyverse.app.domain.usecase.ObserveUserMembershipChangesUseCase
import fr.manyverse.app.presentation.state.AsyncUiState
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectIndexed

data class UserActiveTableInfo(
    val table: GameTable,
    val role: MemberRole
)

class UserTablesViewModel(
    private val getAllUserActiveTablesUseCase: GetAllUserActiveTablesUseCase,
    private val observeUserMembershipChangesUseCase: ObserveUserMembershipChangesUseCase
) : BaseViewModel() {

    private val _userActiveTablesState =
        MutableStateFlow<AsyncUiState<List<UserActiveTableInfo>>>(AsyncUiState.Loading)
    val userActiveTablesState: StateFlow<AsyncUiState<List<UserActiveTableInfo>>> =
        _userActiveTablesState.asStateFlow()

    fun loadAllUserActiveTables(userId: String) {
        launchAsyncMapped(_userActiveTablesState, { fetchTables(userId) }) { it }
    }

    /**
     * Un joueur éjecté revient à l'accueil dès l'événement PLAYER_KICKED, avant que le MJ ait
     * supprimé sa ligne ou la table : la liste chargée à ce moment contient encore la table. Elle
     * est donc rechargée, sans indicateur, à chaque arrivée ou départ de l'une de ses tables.
     *
     * Suspend tant que l'accueil est affiché : l'écoute garde la websocket Realtime ouverte, elle
     * s'arrête avec lui.
     */
    suspend fun watchMembershipChanges(userId: String) {
        observeUserMembershipChangesUseCase(userId).collectIndexed { index, _ ->
            // Le premier signal est l'abonnement, lancé avec le chargement de l'accueil : la liste
            // est à jour, sauf si ce chargement a échoué (hors ligne), qu'il rattrape alors
            if (index == 0 && _userActiveTablesState.value !is AsyncUiState.Error) return@collectIndexed
            launchAsyncSilent(_userActiveTablesState) { fetchTables(userId) }
        }
    }

    private suspend fun fetchTables(userId: String): Result<List<UserActiveTableInfo>> =
        getAllUserActiveTablesUseCase(userId)
            .mapCatching { tablesWithRoles ->
                tablesWithRoles.map { (table, role) ->
                    UserActiveTableInfo(table = table, role = role)
                }
            }
}
